/**
 * Drawing the groups a view is grouped by.
 *
 * The server does the grouping: a view with `group_bys` returns its rows already
 * ordered by those fields, so there is no grouped shape to fetch and nothing
 * here asks for one. What the client does is find the *boundaries* -- where one
 * group ends and the next begins -- by walking the rows it holds and comparing
 * consecutive ones on the group-by fields.
 *
 * The comparison is the field type's own `isEqual`, which is why this can be a
 * pure function at all: two select options are the same group when the field
 * says so, not when their JSON matches.
 */
import type { Field, RowData, ViewGroupBy } from '@/api/types'
import { fieldKey } from '@/api/types'
import { behaviorOf } from '../fields/behavior'

/**
 * How many rows each distinct group holds, across the whole table.
 *
 * Keyed by the group-by field's column name; each entry carries the values of
 * every group-by field up to and including that level, plus the count. It rides
 * on the rows response, and the values are the field types' own serialized
 * representations -- which is what lets them be compared against the row values
 * with the field's own equality rather than with a rule invented here.
 *
 * Declared here rather than taken from the contract even though it is in it: the
 * generated type is `unknown` per value, which is all the schema can honestly
 * say, so the two are the same shape and this one has a name.
 */
export type GroupByMetadata = Record<string, readonly Record<string, unknown>[]>

/** The width a group column takes when the view has not said. */
export const DEFAULT_GROUP_WIDTH = 200

export const groupWidthOf = (groupBy: ViewGroupBy): number =>
  groupBy.width ?? DEFAULT_GROUP_WIDTH

/**
 * How much room the group columns take, which everything to their right is
 * offset by.
 *
 * Zero for a view that is not grouped, so an ungrouped grid is laid out exactly
 * as it was before groups existed rather than being laid out around an empty
 * column of nothing.
 */
export function groupsWidth(groupBys: readonly ViewGroupBy[]): number {
  return groupBys.reduce((total, groupBy) => total + groupWidthOf(groupBy), 0)
}

/**
 * The values that identify one entry, as a string that can be compared.
 *
 * Everything but the count: an entry *is* its path through the group by fields,
 * and the count is what the server says about it. Both sides of a merge come
 * out of the same serializer, so comparing the JSON is comparing what the field
 * type would compare -- there is no field here to ask, and no need for one.
 */
function pathOf(entry: Record<string, unknown>): string {
  return JSON.stringify(
    Object.keys(entry)
      .filter((key) => key !== 'count')
      .sort()
      .map((key) => [key, entry[key]]),
  )
}

/**
 * Folds the counts from one page into the ones already held.
 *
 * A response only counts the groups the rows *in that page* fall into, so a
 * window assembled from three requests has been told about each of its groups
 * once. Replacing rather than merging would blank the count on every group but
 * the most recently fetched, which reads as numbers flickering away as somebody
 * scrolls.
 *
 * A group the new page counts again wins, because it is the more recent answer
 * about a number that changes. The existing object is returned untouched when
 * the page said nothing new, so a store holding this does not re-render for a
 * fetch that told it what it already knew.
 */
export function mergeGroupByMetadata(
  existing: GroupByMetadata,
  incoming: GroupByMetadata,
): GroupByMetadata {
  let changed = false
  const merged: Record<string, readonly Record<string, unknown>[]> = { ...existing }

  for (const [key, entries] of Object.entries(incoming)) {
    const held = existing[key] ?? []
    const byPath = new Map(held.map((entry) => [pathOf(entry), entry]))

    for (const entry of entries) {
      const path = pathOf(entry)
      const before = byPath.get(path)
      if (before !== undefined && before['count'] === entry['count']) continue
      byPath.set(path, entry)
      changed = true
    }

    merged[key] = [...byPath.values()]
  }

  return changed ? merged : existing
}

/** One run of consecutive rows sharing a value at one grouping level. */
export interface GroupSpan {
  /** Absolute index of the first row in the run. */
  start: number
  /** How many rows the run covers *of those currently held*. */
  rows: number
  value: unknown
  /**
   * Rows in the whole group, or null when the metadata does not say.
   *
   * Distinct from the run length: a group may extend far past what is buffered,
   * and reporting the run as the count would tell somebody a group of four
   * thousand holds forty.
   */
  count: number | null
}

/**
 * Whether two rows belong to the same group at a given level.
 *
 * Compares every field from the first group-by down to this one, because
 * grouping is nested: two rows share a second-level group only if they also
 * share the first. Comparing this level alone would merge two runs that happen
 * to hold the same value under different parents.
 */
function sameGroup(
  groupBys: readonly ViewGroupBy[],
  level: number,
  fields: readonly Field[],
  a: RowData | undefined,
  b: RowData | undefined,
): boolean {
  if (a === undefined || b === undefined) return false

  return groupBys.slice(0, level + 1).every((groupBy) => {
    const field = fields.find((one) => one.id === groupBy.field)
    // A group by on a field this reader cannot see: the rows are still ordered
    // by it server-side, but nothing here can tell one group from another, so
    // no two rows are ever the same group and every row starts its own run.
    if (field === undefined) return false
    const key = fieldKey(field.id)
    return behaviorOf(field.type).isEqual(field, a[key], b[key])
  })
}

/**
 * The total size of the group a row belongs to, from the metadata.
 *
 * Matched by comparing every group-by value up to this level, which is what the
 * entries carry. Null rather than zero when nothing matches: the metadata is
 * absent for a view that has just been grouped and has not been re-fetched, and
 * a group labelled "0" would be a group with a row visibly in it.
 */
function countFor(
  groupBys: readonly ViewGroupBy[],
  level: number,
  fields: readonly Field[],
  row: RowData,
  metadata: GroupByMetadata,
): number | null {
  const own = groupBys[level]
  if (own === undefined) return null
  const entries = metadata[fieldKey(own.field)]
  if (entries === undefined) return null

  const levels = groupBys.slice(0, level + 1).flatMap((groupBy) => {
    const field = fields.find((one) => one.id === groupBy.field)
    return field === undefined ? [] : [field]
  })
  if (levels.length !== level + 1) return null

  const match = entries.find((entry) =>
    levels.every((field) => {
      const key = fieldKey(field.id)
      return behaviorOf(field.type).isEqual(field, entry[key], row[key])
    }),
  )
  const count = match?.['count']
  return typeof count === 'number' ? count : null
}

/**
 * The runs to draw beside the rows, one array per grouping level.
 *
 * Computed over the rows on screen rather than the whole table, for the same
 * reason the rows themselves are: only a slice is ever held. A row inside the
 * range that has not arrived yet ends the run it was in -- the honest answer,
 * since whether the group continues through it is not knowable until it lands.
 */
export function groupSpans(
  groupBys: readonly ViewGroupBy[],
  fields: readonly Field[],
  metadata: GroupByMetadata,
  range: { start: number; end: number },
  rowAt: (index: number) => RowData | undefined,
): GroupSpan[][] {
  return groupBys.map((_groupBy, level) => {
    const spans: GroupSpan[] = []
    let open: GroupSpan | null = null

    for (let index = range.start; index < range.end; index += 1) {
      const row = rowAt(index)
      // A hole in the buffer. Nothing closes the open run here because nothing
      // needs to: the next row that does arrive compares itself against this
      // hole, and no row is ever the same group as one that is not there.
      if (row === undefined) continue

      const own = groupBys[level]
      const key =
        own === undefined ? undefined : fields.find((one) => one.id === own.field)

      if (open !== null && sameGroup(groupBys, level, fields, rowAt(index - 1), row)) {
        open.rows += 1
        continue
      }

      open = {
        start: index,
        rows: 1,
        value: key === undefined ? undefined : row[fieldKey(key.id)],
        count: countFor(groupBys, level, fields, row, metadata),
      }
      spans.push(open)
    }

    return spans
  })
}

/**
 * The rows that end a group at the deepest level.
 *
 * Only the deepest, because that is the one whose divider has to line up with
 * the row's own: an outer group always ends where an inner one does, so drawing
 * both would double the line.
 */
export function rowsEndingAGroup(
  groupBys: readonly ViewGroupBy[],
  fields: readonly Field[],
  range: { start: number; end: number },
  rowAt: (index: number) => RowData | undefined,
): Set<number> {
  const deepest = groupBys.length - 1
  const ending = new Set<number>()
  if (deepest < 0) return ending

  for (let index = range.start; index < range.end; index += 1) {
    const row = rowAt(index)
    if (row === undefined) continue
    if (!sameGroup(groupBys, deepest, fields, row, rowAt(index + 1))) {
      ending.add(row.id)
    }
  }

  return ending
}
